import { getAssetUrl } from '@shared/constants';
import {
  getNakedBodyId,
  getHeadAssetName,
  type CharacterRaceId,
  type CharacterSexId,
} from '@shared/types/character';
import { NAKED_BODY_ASSET_PATHS } from '../sprites/BodySpriteManager';
import {
  getWeaponAssetName,
  getWeaponSheetOffsetByName,
} from '../sprites/WeaponSpriteManager';

/**
 * Preview animado de un personaje mirando hacia abajo, dibujado en un <canvas>
 * en 2D. Reusa el MISMO modelo de composición que el juego (`GameScene`):
 *
 *  - body anclado por los pies (origin bottom) en la línea `FOOT_Y`.
 *  - head centrada a `HEAD_OFFSET_FROM_BODY_BOTTOM + HEAD_FRAME_HEIGHT/2` (=42px)
 *    arriba de los pies; helm 8px arriba de la head.
 *  - boots/shield anclados por los pies; weapon por los pies + su offset por
 *    sheet (`WEAPON_SHEET_OFFSETS`).
 *  - orden de capas igual a `updateEquipmentDepth` (DOWN): body → head → helm →
 *    boots → weapon → shield.
 *
 * Y reproduce el efecto de respiración in-game (`GameScene` BREATHING_*):
 * oscilación vertical sutil (~1.5%) de 2s, con 5s de pausa, anclada en los pies.
 *
 * Se usa en la selección de personaje (con el equipo puesto) y en la creación
 * (cuerpo desnudo por raza/sexo). El gráfico es pixel-art → `imageSmoothing`
 * apagado y escala entera para mantenerlo crujiente.
 */

// --- Constantes de composición (espejo de GameScene.ts) ---
const HEAD_OFFSET_FROM_BODY_BOTTOM = 27;
const HEAD_FRAME_HEIGHT = 30; // referencia (los heads reales son 23x30)
const HEAD_OFFSET_FROM_FEET = HEAD_OFFSET_FROM_BODY_BOTTOM + HEAD_FRAME_HEIGHT / 2; // 42
const HELM_OFFSET_ABOVE_HEAD = 8;
const SHIELD_OFFSET_X = 0;

// --- Lienzo nativo (px del juego); se escala ×scale al pintar ---
const NATIVE_W = 64;
// Alto con margen extra debajo de los pies (FOOT_Y) para que el óvalo de
// contacto entre completo sin recortarse.
const NATIVE_H = 96;
const CX = 32; // centro horizontal del esqueleto
const FOOT_Y = 82; // línea de los pies

// --- Respiración (espejo de GameScene.ts, pero al DOBLE de frecuencia: medio
// ciclo) → respira más seguido en el preview. Mismo motion, menos pausa.
const BREATHING_DURATION = 2000;
const BREATHING_INTERVAL = 1500;
const BREATHING_CYCLE = BREATHING_DURATION + BREATHING_INTERVAL;
const BREATHING_SCALE_AMPLITUDE = 0.015;

// --- Sombra de contacto (dropshadow.png, igual que GameScene) ---
const DROPSHADOW_ALPHA = 0.6; // = GameScene.DROPSHADOW_ALPHA
const DROPSHADOW_W = 36; // ~75% del ancho del body (≈43px)
const DROPSHADOW_H = 18; // dropshadow.png es 2:1 (64x32)
// Centro del óvalo EN la línea de los pies (contacto con el piso).
const DROPSHADOW_RAISE = 0;

export interface CharacterAppearance {
  race?: string;
  sex?: string;
  headId?: number;
  /** Body equipado (armadura) o naked body; si falta se deriva de race/sex. */
  bodyId?: number;
  nakedBodyId?: number;
  weaponId?: number;
  shieldId?: number;
  helmId?: number;
  bootId?: number;
}

interface LayerSpec {
  src: string;
  cols: number;
  rows: number;
  /** Ancla por los pies (true) o por el centro (false). */
  anchorFeet: boolean;
  /** Desplazamiento respecto al esqueleto (px nativos). */
  offsetX: number;
  /** Para anchorFeet: Y de la línea de pies; para centro: Y del centro. */
  anchorY: number;
}

export interface CharacterPreviewOptions {
  /** Factor de escala entero (1 px de juego → N px de pantalla). Default 4. */
  scale?: number;
}

export class CharacterPreview {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private scale: number;
  private appearance: CharacterAppearance = {};
  // Modo vacío: no dibuja nada (ni body ni sombra). Se usa en la pantalla de
  // selección cuando NO hay ningún personaje creado, para que el centro quede
  // vacío en vez de mostrar un cuerpo por defecto sin cabeza.
  private empty = false;

  // Cache de spritesheets (compartido entre redibujos).
  private readonly imgCache = new Map<string, HTMLImageElement>();
  // Óvalo de contacto bajo los pies (mismo asset que el dropshadow in-game).
  // getAssetUrl ya tiene base `/assets`, así que el path va SIN el prefijo `assets/`.
  private readonly dropshadowSrc = getAssetUrl('dropshadow.png');
  // Offset aleatorio del ciclo de respiración (para que no respiren al unísono).
  private readonly breathingOffset = Math.floor(Math.random() * BREATHING_CYCLE);
  private startTime = 0;
  private raf = 0;
  private running = false;

  constructor(canvas: HTMLCanvasElement, options: CharacterPreviewOptions = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.scale = Math.max(1, Math.floor(options.scale ?? 4));
    this.applyCanvasSize();
    canvas.style.imageRendering = 'pixelated';
    canvas.style.display = 'block';
  }

  /** Tamaño en píxeles de pantalla del preview a la escala actual. */
  get displayWidth(): number {
    return NATIVE_W * this.scale;
  }
  get displayHeight(): number {
    return NATIVE_H * this.scale;
  }

  /**
   * Distancia (px de pantalla, a la escala actual) entre la línea de los pies
   * (= borde inferior del cuerpo) y el fondo del canvas. Sirve para pegar el
   * nombre a los pies igual que in-game: el label arranca en el borde inferior
   * del cuerpo (ver GameScene.addPlayerNameText), no debajo del margen del canvas.
   */
  get footToBottomPx(): number {
    return (NATIVE_H - FOOT_Y) * this.scale;
  }

  private applyCanvasSize(): void {
    this.canvas.width = NATIVE_W * this.scale;
    this.canvas.height = NATIVE_H * this.scale;
  }

  setScale(scale: number): void {
    this.scale = Math.max(1, Math.floor(scale));
    this.applyCanvasSize();
  }

  setAppearance(appearance: CharacterAppearance): void {
    this.empty = false;
    this.appearance = { ...appearance };
    // Pre-cargar las imágenes nuevas (redibuja al cargar cada una).
    for (const layer of this.buildLayers()) {
      this.getImage(layer.src);
    }
  }

  /**
   * Vacía el preview: no dibuja body/head/sombra (canvas transparente). Para la
   * pantalla de selección sin personajes creados. Se revierte con setAppearance().
   */
  setEmpty(): void {
    this.empty = true;
    const ctx = this.ctx;
    if (ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /**
   * Resuelve cuando TODAS las capas de la apariencia actual (+ la sombra de contacto)
   * están cargadas, para poder revelar el muñeco ya compuesto en vez de verlo aparecer
   * por partes. Si una imagen falla o tarda más que `timeoutMs`, resuelve igual (no
   * bloquea el reveal por un asset roto o lento). Llamar DESPUÉS de setAppearance.
   */
  whenReady(timeoutMs = 4000): Promise<void> {
    const srcs = [this.dropshadowSrc, ...this.buildLayers().map((l) => l.src)];
    const all = Promise.all(
      srcs.map(
        (src) =>
          new Promise<void>((resolve) => {
            this.getImage(src); // asegura crear el Image y arrancar la carga
            const img = this.imgCache.get(src);
            // `complete` es true tanto si cargó como si ya falló → no bloquea.
            if (!img || img.complete) {
              resolve();
              return;
            }
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          })
      )
    ).then(() => undefined);
    return Promise.race([all, new Promise<void>((r) => window.setTimeout(r, timeoutMs))]);
  }

  /** Arranca el loop de animación (respiración). Idempotente. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.startTime = 0;
    const tick = (now: number): void => {
      if (!this.running) return;
      if (!this.startTime) this.startTime = now;
      this.draw(now - this.startTime);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  destroy(): void {
    this.stop();
    this.imgCache.clear();
  }

  // -------------------------------------------------------------------------
  // Resolución de assets
  // -------------------------------------------------------------------------

  private resolvedBodyId(): number {
    const a = this.appearance;
    if (a.bodyId && a.bodyId > 0) return a.bodyId;
    if (a.nakedBodyId && a.nakedBodyId > 0) return a.nakedBodyId;
    return getNakedBodyId(a.race as CharacterRaceId, a.sex as CharacterSexId);
  }

  private bodyAssetUrl(bodyId: number): string {
    // Naked bodies (101–112) viven en .../body/races/<raza>_<sexo>.png;
    // las armaduras en .../body/anims/body<id>.png (ver BodySpriteManager).
    // getAssetUrl ya tiene base `/assets`; NAKED_BODY_ASSET_PATHS trae el prefijo
    // `assets/`, así que se lo quitamos para no duplicar.
    const nakedPath = NAKED_BODY_ASSET_PATHS[bodyId]?.replace(/^assets\//, '');
    return getAssetUrl(nakedPath ?? `entities/body/anims/body${bodyId}.png`);
  }

  /** Capas a dibujar, en orden back→front (igual que updateEquipmentDepth, DOWN). */
  private buildLayers(): LayerSpec[] {
    const a = this.appearance;
    const layers: LayerSpec[] = [];

    // Body (siempre).
    const bodyId = this.resolvedBodyId();
    layers.push({
      src: this.bodyAssetUrl(bodyId),
      cols: 6,
      rows: 4,
      anchorFeet: true,
      offsetX: 0,
      anchorY: FOOT_Y,
    });

    // Head.
    if (a.headId && a.headId > 0) {
      layers.push({
        src: getAssetUrl(`entities/head/${getHeadAssetName(a.headId)}.png`),
        cols: 4,
        rows: 1,
        anchorFeet: false,
        offsetX: 0,
        anchorY: FOOT_Y - HEAD_OFFSET_FROM_FEET,
      });
    }

    // Helm (encima de la head).
    if (a.helmId && a.helmId > 0) {
      layers.push({
        src: getAssetUrl(`helm/helm${a.helmId}.png`),
        cols: 4,
        rows: 1,
        anchorFeet: false,
        offsetX: 0,
        anchorY: FOOT_Y - HEAD_OFFSET_FROM_FEET - HELM_OFFSET_ABOVE_HEAD,
      });
    }

    // Boots.
    if (a.bootId && a.bootId > 0) {
      layers.push({
        src: getAssetUrl(`boot/boot${a.bootId}.png`),
        cols: 4,
        rows: 1,
        anchorFeet: true,
        offsetX: 0,
        anchorY: FOOT_Y,
      });
    }

    // Weapon (con offset fino por sheet).
    if (a.weaponId && a.weaponId > 0) {
      const assetName = getWeaponAssetName(a.weaponId);
      const off = getWeaponSheetOffsetByName(assetName);
      layers.push({
        src: getAssetUrl(`weapon/anims/${assetName}.png`),
        cols: 6,
        rows: 4,
        anchorFeet: true,
        offsetX: off.x,
        anchorY: FOOT_Y + off.y,
      });
    }

    // Shield.
    if (a.shieldId && a.shieldId > 0) {
      layers.push({
        src: getAssetUrl(`shield/shield${a.shieldId}.png`),
        cols: 6,
        rows: 4,
        anchorFeet: true,
        offsetX: SHIELD_OFFSET_X,
        anchorY: FOOT_Y,
      });
    }

    return layers;
  }

  private getImage(src: string): HTMLImageElement | null {
    let img = this.imgCache.get(src);
    if (!img) {
      img = new Image();
      this.imgCache.set(src, img);
      // Redibujar cuando cargue (el draw siguiente del loop ya la tomará, pero
      // si el loop no está corriendo igual forzamos un redraw puntual).
      img.addEventListener('load', () => {
        if (!this.running) this.draw(0);
      });
      img.src = src;
    }
    if (img.complete && img.naturalWidth > 0) return img;
    return null;
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  private breathingScaleY(elapsed: number): number {
    const cycleTime = (elapsed + this.breathingOffset) % BREATHING_CYCLE;
    if (cycleTime >= BREATHING_DURATION) return 1.0;
    const progress = cycleTime / BREATHING_DURATION; // 0..1
    return 1.0 + Math.sin(progress * Math.PI) * BREATHING_SCALE_AMPLITUDE;
  }

  private draw(elapsed: number): void {
    const ctx = this.ctx;
    if (!ctx) return;

    // Modo vacío: canvas transparente, sin body ni sombra.
    if (this.empty) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }

    const breathe = this.breathingScaleY(elapsed);
    const sx = this.scale; // sin respiración en X
    const sy = this.scale * breathe; // estiramiento vertical
    // Anclar los pies: el punto FOOT_Y debe quedar fijo aunque cambie sy.
    const ty = FOOT_Y * this.scale * (1 - breathe);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.imageSmoothingEnabled = false;

    // Sombra de contacto: NO respira (escala plana), anclada a los pies.
    const shadowImg = this.getImage(this.dropshadowSrc);
    if (shadowImg) {
      ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);
      ctx.globalAlpha = DROPSHADOW_ALPHA;
      ctx.drawImage(
        shadowImg,
        0,
        0,
        shadowImg.width,
        shadowImg.height,
        Math.round(CX - DROPSHADOW_W / 2),
        Math.round(FOOT_Y - DROPSHADOW_RAISE - DROPSHADOW_H / 2),
        DROPSHADOW_W,
        DROPSHADOW_H
      );
      ctx.globalAlpha = 1;
    }

    ctx.setTransform(sx, 0, 0, sy, 0, ty);

    for (const layer of this.buildLayers()) {
      const img = this.getImage(layer.src);
      if (!img) continue;
      const fw = img.width / layer.cols;
      const fh = img.height / layer.rows;
      // Frame DOWN-idle: siempre la celda (0,0) — fila 0 = abajo, col 0 = idle.
      const dx = Math.round(CX + layer.offsetX - fw / 2);
      const dy = layer.anchorFeet
        ? Math.round(layer.anchorY - fh)
        : Math.round(layer.anchorY - fh / 2);
      ctx.drawImage(img, 0, 0, fw, fh, dx, dy, fw, fh);
    }
  }
}
